import React, { memo } from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import type { TreeBlock, TreeNode } from '../../../types/backend';
import BlockShell from './BlockShell';
import { chartPalette } from './blockFormat';

interface Props {
  block: TreeBlock;
}

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default' | 'info'> = {
  ok: 'success',
  healthy: 'success',
  active: 'success',
  degraded: 'warning',
  warning: 'warning',
  failed: 'error',
  error: 'error',
  critical: 'error',
};

function summarize(roots: TreeNode[]): string {
  const lines: string[] = [];
  const walk = (n: TreeNode, depth: number) => {
    lines.push(`${'— '.repeat(depth)}${n.label}`);
    (n.children ?? []).forEach((c) => walk(c, depth + 1));
  };
  roots.forEach((r) => walk(r, 0));
  return `Tree hierarchy: ${lines.join('; ')}.`;
}

const TreeNodeView: React.FC<{ node: TreeNode; depth: number; colors: string[] }> = ({
  node,
  depth,
  colors,
}) => {
  const accent = colors[depth % colors.length];
  const children = node.children ?? [];
  const statusColor = node.status ? (STATUS_COLORS[node.status.toLowerCase()] ?? 'default') : undefined;

  return (
    <Box component="li" sx={{ listStyle: 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: accent, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {node.label}
        </Typography>
        {node.status && (
          <Chip
            label={node.status}
            color={statusColor}
            size="small"
            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 500 }}
          />
        )}
        {node.detail && (
          <Typography variant="caption" color="text.secondary">
            {node.detail}
          </Typography>
        )}
      </Box>
      {children.length > 0 && (
        <Box
          component="ul"
          sx={{ listStyle: 'none', m: 0, pl: 2, ml: '3px', borderLeft: '2px solid', borderColor: 'divider' }}
        >
          {children.map((child, i) => (
            <TreeNodeView key={i} node={child} depth={depth + 1} colors={colors} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const TreeBlockView: React.FC<Props> = memo(({ block }) => {
  const theme = useTheme();
  const colors = chartPalette(theme);
  const summary = summarize(block.roots);

  return (
    <BlockShell bodyPadding={2}>
      <Box role="img" aria-label={summary}>
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {block.roots.map((root, i) => (
            <TreeNodeView key={i} node={root} depth={0} colors={colors} />
          ))}
        </Box>
        <Box component="span" sx={visuallyHidden}>
          {summary}
        </Box>
      </Box>
      {block.caption && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {block.caption}
        </Typography>
      )}
    </BlockShell>
  );
});

TreeBlockView.displayName = 'TreeBlockView';
export default TreeBlockView;
